/**
 * Wishlist Manager - Moon Moon Agency
 * Pilote la wishlist (app Wishlist Power) via son API JavaScript
 * (window.ooo.wishlist.functions), sans aucun custom element <ooo-wl-*>.
 *
 * Responsabilites :
 *  - Etat central : Set("handle|variantId") synchronise depuis getContent() + events app
 *  - Boutons coeur [data-wishlist-toggle] : toggle direct OU ouverture du QAP wishlist
 *  - Compteur header [data-wishlist-count]
 *  - Etat des cards (.mm-pcard--in-wishlist) suivant le swatch actif
 *  - Rendu de la page favoris [data-wishlist-grid] (fetch cards via ?view=wishlist-card)
 */

/*********************************************************************************************
 * ❤ WISHLIST MANAGER
 */
const WishlistManager = {
	config: {
		selectors: {
			toggle: '[data-wishlist-toggle]',
			count: '[data-wishlist-count]',
			grid: '[data-wishlist-grid]',
			empty: '[data-wishlist-empty]',
			login: '[data-wishlist-login]',
			card: '.mm-pcard',
			activeSwatch: '.mm-pcard__swatch--active[data-variant-id]',
		},
		classes: {
			inWishlist: 'mm-pcard--in-wishlist',
			btnActive: 'mm-wishlist-btn--active',
			loading: 'mm-loading',
		},
		cardView: 'wishlist-card',
		maxPollAttempts: 20,
		pollDelay: 250,
	},

	/* Set of "handle|variantId" strings present in wishlist (variantId='' if null) */
	_entries: new Set(),
	_initialized: false,
	_pollAttempts: 0,

	init() {
		if (this._initialized) return;

		/* Wait until window.ooo.wishlist.functions is available (app bundle loads via content_for_header) */
		if (this._fns()) {
			this._start();
		} else if (this._pollAttempts < this.config.maxPollAttempts) {
			this._pollAttempts++;
			setTimeout(() => this.init(), this.config.pollDelay);
		} else {
			window.log('warn', '❤ [WISHLIST]', 'API window.ooo.wishlist.functions never became available');
		}
	},

	_start() {
		this._initialized = true;
		this._bindEvents();
		this._syncFromServer();
		window.log('info', '❤ [WISHLIST]', 'Initialized');
	},

	_fns() {
		return window.ooo?.wishlist?.functions || null;
	},

	_bindEvents() {
		/* App events — keep state, header count, cards and page in sync */
		document.addEventListener('ooo-wishlist:product-added', (e) => this._handleEvent(e, true));
		document.addEventListener('ooo-wishlist:product-removed', (e) => this._handleEvent(e, false));

		/* Coeur click — delegation */
		document.addEventListener('click', (e) => {
			const btn = e.target.closest(this.config.selectors.toggle);
			if (!btn) return;
			e.preventDefault();
			this._onToggleClick(btn);
		});

		/* Bouton "Se connecter" — ouvre le flow natif <shopify-account> (fallback href si echec) */
		document.addEventListener('click', (e) => {
			const loginBtn = e.target.closest(this.config.selectors.login);
			if (!loginBtn) return;
			if (this._openAccountDialog()) e.preventDefault();
		});

		/* Card swatch change — re-evaluate .mm-pcard--in-wishlist for the active variant */
		document.addEventListener('click', (e) => {
			if (!e.target.closest('.mm-pcard__swatch[data-variant-id]')) return;
			/* Defer so swatch--active class is applied first */
			setTimeout(() => {
				const card = e.target.closest(this.config.selectors.card);
				if (card) this._applyToCard(card);
			}, 0);
		});

		/* PDP variant change — keep the page button's variant-id and state in sync */
		document.addEventListener('product:variant-input-updated', (e) => {
			const variantId = e.detail?.data?.variantId;
			if (!variantId) return;
			document.querySelectorAll(`${this.config.selectors.toggle}[data-wishlist-mode="toggle"]`).forEach((btn) => {
				btn.dataset.variantId = variantId;
				const active = this._isInWishlist(btn.dataset.handle, Number(variantId));
				this._setButtonState(btn, active);
			});
		});
	},

	/* ----- State ----- */

	_key(handle, variantId) {
		return `${handle}|${variantId == null ? '' : variantId}`;
	},

	_isInWishlist(handle, variantId) {
		/* Match exact variant OR generic product (variantId = null) */
		return this._entries.has(this._key(handle, variantId)) || this._entries.has(this._key(handle, null));
	},

	/* Produit en favoris, quel que soit le variant retenu */
	_hasAnyVariant(handle) {
		const prefix = `${handle}|`;
		for (const key of this._entries) {
			if (key.startsWith(prefix)) return true;
		}
		return false;
	},

	async _syncFromServer() {
		const fns = this._fns();
		if (!fns) return;
		try {
			const content = await fns.getContent(false);
			this._entries.clear();
			(content?.products || []).forEach((p) => {
				if (p.handle) this._entries.add(this._key(p.handle, p.variant_id));
			});
			this._applyToAllCards();
			this._updateCount();
		} catch (e) {
			window.log('warn', '❤ [WISHLIST]', 'Sync failed', { error: e.message });
		}
	},

	_handleEvent(e, isAdd) {
		const handle = e.detail?.product?.handle;
		if (!handle) return;
		const variantId = e.detail?.product?.variant_id ?? null;
		const key = this._key(handle, variantId);
		if (isAdd) this._entries.add(key);
		else this._entries.delete(key);

		this._applyToAllCards(handle);
		this._updateCount(e.detail?.wishlist_products_count);
		this._updateButtons(handle);
		if (!isAdd) this._removeCardFromPage(handle, variantId);

		window.emit?.('global:wishlist-updated', { handle, variantId, added: isAdd, count: this._entries.size });
	},

	/* ----- Connexion (flow natif <shopify-account>) ----- */

	_openAccountDialog() {
		const account = document.querySelector('shopify-account');
		if (!account) return false;
		/* Cliquer le bouton interne du shadow DOM (ouvre le <dialog> de connexion natif) */
		const innerBtn = account.shadowRoot?.querySelector('.account-button');
		if (innerBtn) { innerBtn.click(); return true; }
		/* Fallback : cliquer l'avatar slotte (light DOM) */
		const slotted = account.querySelector('[slot="signed-out-avatar"]');
		if (slotted) { slotted.click(); return true; }
		return false;
	},

	/* ----- Coeur toggle ----- */

	_onToggleClick(btn) {
		const mode = btn.dataset.wishlistMode || 'toggle';
		if (mode === 'panel') {
			/* Card de collection : ouvre le QAP en mode wishlist (choix variante) */
			const card = btn.closest(this.config.selectors.card);
			if (card && window.QuickAddPanelManager?.open) {
				window.QuickAddPanelManager.open(card, 'wishlist');
			}
			return;
		}
		/* mode 'toggle' : PDP + page favoris — add/remove direct */
		this._toggle(btn);
	},

	async _toggle(btn) {
		const fns = this._fns();
		if (!fns?.isWishlistAccessible?.()) {
			window.log('warn', '❤ [WISHLIST]', 'Wishlist not accessible');
			return;
		}
		const handle = btn.dataset.handle;
		if (!handle) return;
		const variantId = this._btnVariantId(btn);

		/* Mise a jour optimiste : bascule l'etat visuel immediatement depuis l'etat local connu,
		   sans attendre le round-trip async de l'app (containsProduct + add/removeProduct). */
		const wasInWishlist = this._isInWishlist(handle, variantId);
		this._setButtonState(btn, !wasInWishlist);
		btn.disabled = true;

		try {
			if (wasInWishlist) await fns.removeProduct(handle, variantId);
			else await fns.addProduct(handle, variantId);
			/* Etat local + reste de l'UI (cards, compteur) rafraichis par les events app (_handleEvent) */
		} catch (e) {
			window.log('error', '❤ [WISHLIST]', 'Toggle failed', { error: e.message });
			/* Rollback visuel si l'appel echoue */
			this._setButtonState(btn, wasInWishlist);
		} finally {
			btn.disabled = false;
		}
	},

	_btnVariantId(btn) {
		/* PDP button keeps its variant-id in sync via mm_main_product.js */
		const raw = btn.dataset.variantId;
		return raw ? Number(raw) : null;
	},

	/* ----- Buttons & cards ----- */

	_updateButtons(handle) {
		document.querySelectorAll(`${this.config.selectors.toggle}[data-handle="${handle}"]`).forEach((btn) => {
			/* Card buttons (mode panel) reflect the product; PDP buttons use their own variant-id */
			if ((btn.dataset.wishlistMode || 'toggle') === 'panel') {
				this._setButtonState(btn, this._hasAnyVariant(handle));
				return;
			}
			this._setButtonState(btn, this._isInWishlist(handle, this._btnVariantId(btn)));
		});
	},

	_setButtonState(btn, active) {
		btn.classList.toggle(this.config.classes.btnActive, active);
		btn.setAttribute('aria-pressed', String(active));
	},

	_applyToAllCards(filterHandle = null) {
		document.querySelectorAll(this.config.selectors.card).forEach((card) => {
			const handle = card.dataset.wishlistHandle;
			if (!handle) return;
			if (filterHandle && handle !== filterHandle) return;
			this._applyToCard(card, handle);
		});
	},

	_applyToCard(card, handle = null) {
		handle = handle || card.dataset.wishlistHandle;
		if (!handle) return;
		const activeSwatch = card.querySelector(this.config.selectors.activeSwatch);
		const variantId = activeSwatch ? Number(activeSwatch.dataset.variantId) : null;
		const btn = card.querySelector(this.config.selectors.toggle);

		/**
		 * Mode 'panel' : le coeur represente le PRODUIT (il ouvre le panel de choix couleur),
		 * pas le swatch actif. La couleur mise en favoris depuis le panel n'est pas forcement
		 * le swatch actif de la card : tester le variant laisserait le coeur vide apres ajout.
		 */
		const isPanel = (btn?.dataset?.wishlistMode || 'toggle') === 'panel';
		const inWishlist = isPanel
			? this._hasAnyVariant(handle)
			: this._isInWishlist(handle, variantId);

		card.classList.toggle(this.config.classes.inWishlist, inWishlist);
		/* Sync the card coeur button (mode panel) for visual feedback */
		if (btn) this._setButtonState(btn, inWishlist);
	},

	/* ----- Header count ----- */

	_updateCount(explicitCount = null) {
		const count = explicitCount != null ? explicitCount : this._entries.size;
		document.querySelectorAll(this.config.selectors.count).forEach((el) => {
			el.textContent = count;
			el.hidden = count === 0;
		});
	},

	/* ----- Page favoris ----- */

	async renderPage() {
		const grid = document.querySelector(this.config.selectors.grid);
		if (!grid) return;

		/* Ensure API is ready before rendering */
		if (!this._fns()) {
			if (this._pollAttempts < this.config.maxPollAttempts) {
				this._pollAttempts++;
				setTimeout(() => this.renderPage(), this.config.pollDelay);
			}
			return;
		}

		const fns = this._fns();
		let products = [];
		try {
			const content = await fns.getContent(false);
			products = content?.products || [];
		} catch (e) {
			window.log('warn', '❤ [WISHLIST]', 'getContent failed on page', { error: e.message });
		}

		if (!products.length) {
			this._showEmpty(grid, true);
			return;
		}
		this._showEmpty(grid, false);

		const root = window.Shopify?.routes?.root || '/';
		const cards = await Promise.all(
			products.map((p) => {
				/* Pass the exact favorited variant so the card shows the right size/color/price */
				const variantParam = p.variant_id ? `variant=${p.variant_id}&` : '';
				return fetch(`${root}products/${p.handle}?${variantParam}view=${this.config.cardView}`)
					.then((res) => {
						if (!res.ok) throw new Error(`HTTP ${res.status}`);
						return res.text();
					})
					.then((html) => {
						const temp = document.createElement('div');
						temp.innerHTML = html.trim();
						return temp.querySelector('.mm-pcard');
					})
					.catch((e) => {
						window.log('warn', '❤ [WISHLIST]', 'Card fetch failed', { handle: p.handle, error: e.message });
						return null;
					});
			})
		);

		grid.innerHTML = '';
		cards.filter(Boolean).forEach((card) => grid.appendChild(card));

		/* Activate add-to-cart forms + swatches on freshly injected cards */
		if (typeof window.initAddToCartForms === 'function') window.initAddToCartForms();
		if (typeof window.CardSwatchManager?.setup === 'function') window.CardSwatchManager.setup();

		this._applyToAllCards();

		if (!grid.children.length) this._showEmpty(grid, true);

		window.log('info', '❤ [WISHLIST]', 'Page rendered', { count: grid.children.length });
	},

	_removeCardFromPage(handle, variantId) {
		const grid = document.querySelector(this.config.selectors.grid);
		if (!grid) return;
		grid.querySelectorAll(`.mm-pcard[data-wishlist-handle="${handle}"]`).forEach((card) => {
			/* On the wishlist page, a removal targets the whole product line */
			card.remove();
		});
		if (!grid.children.length) this._showEmpty(grid, true);
	},

	_showEmpty(grid, isEmpty) {
		const empty = document.querySelector(this.config.selectors.empty);
		if (empty) empty.hidden = !isEmpty;
		grid.hidden = isEmpty;
	},
};

window.WishlistManager = WishlistManager;

/* Auto-init */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		WishlistManager.init();
		WishlistManager.renderPage();
	});
} else {
	WishlistManager.init();
	WishlistManager.renderPage();
}
